'use client';

import React from 'react';
import Link from 'next/link';
import { useCustomerly } from 'react-live-chat-customerly';
import { featuresGroup, primaryLinks, solutionsGroup } from '@/content/navigation';
import type { NavGroup } from '@/content/navigation';
import { SIGNIN_URL } from '@/lib/urls';
import { useSignupCtas } from '@/components/utils/SignupModeProvider';
import { useCtaLinkProps } from '@/components/utils/useCtaLinkProps';

interface MobileNavDrawerProps {
  onLinkClick: () => void;
  /** Senza la CTA in fondo: vedi `hideCta` di NavigationBar */
  hideCta?: boolean;
}

/** Le due righe di servizio in cima: stessa forma, una accanto all'altra. */
const utilityClasses =
  'flex items-center justify-between gap-3 rounded-ds-sm border border-edge-soft bg-mist px-4 py-3.5 ds-focus';
const utilityQuestion = 'text-[0.9375rem] font-medium text-ink-muted';
const utilityAction = 'text-[0.9375rem] font-semibold text-accent';

const itemClasses =
  'block border-b border-edge-soft py-3 text-[1.0625rem] font-semibold text-primary ds-focus rounded-sm ' +
  'last:border-b-0';

/** Gruppo del drawer: tutte le voci sono già aperte, senza accordion da toccare. */
const DrawerGroup = ({ group, onLinkClick }: { group: NavGroup; onLinkClick: () => void }) => (
  <section>
    <h2 className="mb-2 text-[0.6875rem] font-semibold tracking-[0.12em] text-ink-faint uppercase">
      {group.label}
    </h2>
    <div className="flex flex-col">
      {group.items.map(item => (
        <Link key={item.href} href={item.href} onClick={onLinkClick} className={itemClasses}>
          {item.label}
        </Link>
      ))}
      <Link href={group.indexLink.href} onClick={onLinkClick} className={itemClasses}>
        <span className="text-accent">{group.indexLink.label} →</span>
      </Link>
    </div>
  </section>
);

/** Drawer mobile a tutta pagina, con le CTA fissate in fondo. */
const MobileNavDrawer = ({ onLinkClick, hideCta = false }: MobileNavDrawerProps) => {
  const { primary } = useSignupCtas();
  const ctaProps = useCtaLinkProps('mobile-menu');
  const { open: openChat } = useCustomerly();
  const { onClick: onPrimaryClick, ...primaryProps } = ctaProps(primary);

  return (
    <div
      id="mobile-menu"
      /* Il fondo è generoso perché il bollo della chat — 60px a 20px dal
         bordo — sta fisso nell'angolo e coprirebbe la CTA. */
      className="fixed inset-x-0 top-[4.25rem] bottom-0 z-60 flex flex-col gap-5 overflow-y-auto bg-white px-6 pt-5 pb-[6.5rem] lg:hidden"
    >
      {/*
       * Chi è già cliente arriva per entrare, e chi ha un problema arriva per
       * chiedere: su mobile la barra non ha spazio per «Accedi», e in fondo al
       * drawer — dopo dodici link — sarebbero sotto la piega. Qui sono le prime
       * due cose che si vedono, staccate dalla navigazione.
       */}
      <div className="flex flex-col gap-2">
        <a href={SIGNIN_URL} onClick={onLinkClick} className={utilityClasses}>
          <span className={utilityQuestion}>Già cliente?</span>
          <span className={utilityAction}>Accedi →</span>
        </a>
        <button
          type="button"
          onClick={() => {
            // Prima si chiude il drawer: la chat si apre sopra la pagina, non
            // dietro al menu.
            onLinkClick();
            openChat();
          }}
          className={utilityClasses}
        >
          <span className={utilityQuestion}>Serve aiuto?</span>
          <span className={utilityAction}>Contattaci →</span>
        </button>
      </div>

      <div className="flex flex-col">
        {primaryLinks.map(link => (
          <Link key={link.href} href={link.href} onClick={onLinkClick} className={itemClasses}>
            {link.label}
          </Link>
        ))}
      </div>

      <DrawerGroup group={featuresGroup} onLinkClick={onLinkClick} />
      <DrawerGroup group={solutionsGroup} onLinkClick={onLinkClick} />

      {!hideCta && (
        <div className="mt-auto flex flex-col gap-3 pt-2">
          <a
            {...primaryProps}
            onClick={event => {
              onLinkClick();
              onPrimaryClick?.(event);
            }}
            className="rounded-ds bg-accent px-4 py-3.5 text-center text-base font-semibold text-white ds-focus"
          >
            {primary.label}
          </a>
        </div>
      )}
    </div>
  );
};

export default MobileNavDrawer;
